import React, { useEffect, useRef, useState } from "react";
import { Button, Dialog } from "./ui";

const VIEW = 280;
const OUTPUT = 512;

/**
 * Position a photo inside a circle, then keep exactly what the circle shows.
 *
 * Profile pictures are only ever drawn round, so a straight upload leaves the
 * member guessing which part of the photo survives. The app crops the same
 * way: drag to move, slide to zoom, save a square that the circle fills.
 */
const PhotoCropper = ({ file, onCancel, onDone }) => {
  const canvas = useRef(null);
  const [image, setImage] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // The scale at which the photo's shorter side just fills the circle.
  const base = image ? VIEW / Math.min(image.width, image.height) : 1;

  const draw = (target, size) => {
    const ctx = target.getContext("2d");
    const ratio = size / VIEW;
    const scale = base * zoom * ratio;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(
      image,
      size / 2 - (image.width * scale) / 2 + offset.x * ratio,
      size / 2 - (image.height * scale) / 2 + offset.y * ratio,
      image.width * scale,
      image.height * scale
    );
  };

  useEffect(() => {
    if (image && canvas.current) draw(canvas.current, VIEW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image, zoom, offset]);

  // Keep the circle covered: the photo may not be dragged past its edge.
  const clamp = (x, y) => {
    if (!image) return { x, y };
    const width = image.width * base * zoom;
    const height = image.height * base * zoom;
    const maxX = Math.max(0, (width - VIEW) / 2);
    const maxY = Math.max(0, (height - VIEW) / 2);
    return { x: Math.min(maxX, Math.max(-maxX, x)), y: Math.min(maxY, Math.max(-maxY, y)) };
  };

  // Zooming out can leave the photo short of an edge; pull it back in.
  useEffect(() => {
    setOffset((current) => clamp(current.x, current.y));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, image]);

  const pointerDown = (event) => {
    drag.current = { x: event.clientX, y: event.clientY, start: offset };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const pointerMove = (event) => {
    if (!drag.current) return;
    const { x, y, start } = drag.current;
    setOffset(clamp(start.x + event.clientX - x, start.y + event.clientY - y));
  };
  const pointerUp = () => {
    drag.current = null;
  };

  const save = () => {
    const out = document.createElement("canvas");
    out.width = OUTPUT;
    out.height = OUTPUT;
    draw(out, OUTPUT);
    out.toBlob(
      (blob) => onDone(new File([blob], "profile.jpg", { type: "image/jpeg" })),
      "image/jpeg",
      0.9
    );
  };

  return (
    <Dialog
      title="Position your photo"
      onClose={onCancel}
      actions={
        <>
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} disabled={!image}>
            Use photo
          </Button>
        </>
      }
    >
      <p className="sf-muted">Drag to move it, and zoom to fit. Only what is inside the circle is kept.</p>
      <div style={{ display: "flex", justifyContent: "center", margin: "1rem 0" }}>
        <div style={{ position: "relative", width: VIEW, height: VIEW, touchAction: "none" }}>
          <canvas
            ref={canvas}
            width={VIEW}
            height={VIEW}
            style={{ cursor: "grab", display: "block", borderRadius: 8 }}
            onPointerDown={pointerDown}
            onPointerMove={pointerMove}
            onPointerUp={pointerUp}
            onPointerCancel={pointerUp}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.6)",
              border: "2px solid #fff",
              pointerEvents: "none",
            }}
          />
        </div>
      </div>
      <input
        type="range"
        aria-label="Zoom"
        min="1"
        max="4"
        step="0.01"
        value={zoom}
        onChange={(event) => setZoom(Number(event.target.value))}
        style={{ width: "100%" }}
      />
    </Dialog>
  );
};

export default PhotoCropper;
